import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { ConfirmDialog } from '@/components/data/ConfirmDialog';
import { EmptyState } from '@/components/data/EmptyState';
import { AuditChip } from '@/components/audit/AuditChip';
import { ProductForm } from '@/features/commerce/ProductForm';
import { VariantsTab } from '@/features/commerce/VariantsTab';
import { MediaTab } from '@/features/commerce/MediaTab';
import {
  useDeleteProduct,
  useProduct,
  useRestoreProduct,
} from '@/queries/commerce';
import { useApiError } from '@/lib/useApiError';

export const Route = createFileRoute('/_authed/commerce/products/$id')({
  component: ProductDetailScreen,
});

const RESTORE_WINDOW_MS = 30_000;

function ProductDetailScreen() {
  const { id } = Route.useParams();
  const { t } = useTranslation('commerce');
  const { t: tc } = useTranslation('common');
  const product = useProduct(id);
  const del = useDeleteProduct();
  const restore = useRestoreProduct();
  const resolveError = useApiError();

  const [deleting, setDeleting] = React.useState(false);

  if (product.isLoading) {
    return (
      <div className="space-y-3">
        <div className="h-6 w-1/3 animate-pulse rounded bg-[color:var(--color-surface-2)]" />
        <div className="h-32 animate-pulse rounded bg-[color:var(--color-surface-2)]" />
      </div>
    );
  }

  if (product.error || !product.data) {
    const resolved = product.error ? resolveError(product.error) : null;
    return (
      <EmptyState
        variant="error"
        title={t('products.notFound.title')}
        body={resolved?.message ?? t('products.notFound.body')}
        action={
          <Button asChild variant="ghost">
            <Link to="/commerce/products">{t('products.backToList')}</Link>
          </Button>
        }
      />
    );
  }

  const p = product.data;
  const trashed = Boolean(p.deleted_at);

  async function handleDelete() {
    try {
      await del.mutateAsync(p.id);
      const toastId = toast.success(t('products.deleted'), {
        description: t('products.deletedUndo'),
        duration: RESTORE_WINDOW_MS,
        action: {
          label: t('products.undo'),
          onClick: () => void runRestore(toastId),
        },
      });
      setDeleting(false);
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(
        resolved.message,
        resolved.recovery ? { description: resolved.recovery } : undefined
      );
    }
  }

  async function runRestore(toastId?: string | number) {
    try {
      await restore.mutateAsync(p.id);
      if (toastId !== undefined) toast.dismiss(toastId);
      toast.success(t('products.restored'));
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(
        resolved.message,
        resolved.recovery ? { description: resolved.recovery } : undefined
      );
    }
  }

  return (
    <div className="space-y-6">
      <Button asChild variant="ghost" size="sm">
        <Link to="/commerce/products">
          <ArrowLeft aria-hidden className="size-4 rtl:rotate-180" />
          {t('products.backToList')}
        </Link>
      </Button>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">{p.name_en}</h1>
            {trashed ? <Badge tone="danger">{t('products.states.deleted')}</Badge> : null}
            <Badge tone="neutral">{t(`products.availability.${p.availability}`)}</Badge>
          </div>
          {p.name_ar ? (
            <p dir="rtl" className="text-sm text-[color:var(--color-fg-muted)]">
              {p.name_ar}
            </p>
          ) : null}
          <p dir="ltr" className="font-mono text-xs text-[color:var(--color-fg-subtle)]">
            {p.slug}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <AuditChip />
          {trashed ? (
            <Button onClick={() => void runRestore()} loading={restore.isPending} variant="secondary">
              <RotateCcw aria-hidden className="size-4" />
              {t('products.restore')}
            </Button>
          ) : (
            <Button variant="ghost" onClick={() => setDeleting(true)}>
              <Trash2 aria-hidden className="size-4" />
              {tc('actions.cancel', { defaultValue: 'Delete' })}
              {/* visible label below */}
              <span className="sr-only">{t('products.delete')}</span>
            </Button>
          )}
        </div>
      </header>

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">{t('products.tabs.details')}</TabsTrigger>
          <TabsTrigger value="variants">{t('products.tabs.variants')}</TabsTrigger>
          <TabsTrigger value="media">{t('products.tabs.media')}</TabsTrigger>
        </TabsList>
        <TabsContent value="details">
          <Card>
            <CardContent className="pt-6">
              <ProductForm product={p} />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="variants">
          <Card>
            <CardContent className="pt-6">
              <VariantsTab product={p} />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="media">
          <Card>
            <CardContent className="pt-6">
              <MediaTab product={p} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title={t('products.deleteTitle')}
        description={t('products.deleteBody', { name: p.name_en })}
        destructive
        requireType="DELETE"
        confirmLabel={t('products.delete')}
        loading={del.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
